/*
 * selectField — Alpine component for .mh-form-dropdown (single-select).
 *
 * WAI-ARIA combobox + listbox. The trigger is a <button> with
 * role="combobox"; the panel is role="listbox"; each option is a
 * <button role="option"> with aria-selected. Selection lives on a hidden
 * <input type="hidden" name="..."> so the form posts the chosen value
 * natively.
 *
 * Floating-label support: the trigger carries `data-empty="true"` when
 * no value is set, which the CSS uses to drop the label to the centered
 * placeholder position. JS toggles this attribute whenever the selection
 * changes.
 *
 * Keyboard model:
 *   - Trigger: Space/Enter toggles. ArrowDown opens + focuses currently-
 *     selected option (or first); ArrowUp opens + focuses last.
 *   - Panel: ArrowDown/Up roving focus. Home/End jump to first/last.
 *            Enter or Space selects the focused option and closes.
 *            Escape closes + returns focus to the trigger.
 *   - Outside click closes (without selecting).
 */
export default function selectField() {
    return {
        open: false,
        currentLabel: '',
        currentValue: '',

        get expandedAttr() {
            return String(this.open);
        },

        get emptyAttr() {
            return String(this.currentValue === '');
        },

        init() {
            // If a value was pre-selected via the option markup, sync the
            // hidden input + value span to match.
            const preselected = this.getOptions().find(
                (opt) => opt.getAttribute('aria-selected') === 'true',
            );
            if (preselected) {
                this.applySelection(preselected, false);
            }
        },

        destroy() {},

        getOptions() {
            return this.$refs.panel
                ? Array.from(this.$refs.panel.querySelectorAll('[role="option"]'))
                : [];
        },

        focusOption(index) {
            const items = this.getOptions();
            if (items.length === 0) return;
            const wrapped = (index + items.length) % items.length;
            items[wrapped].focus();
        },

        focusFirst() {
            this.focusOption(0);
        },

        focusLast() {
            this.focusOption(-1);
        },

        focusNext() {
            const items = this.getOptions();
            if (items.length === 0) return;
            const active = items.indexOf(document.activeElement);
            this.focusOption(active + 1);
        },

        focusPrev() {
            const items = this.getOptions();
            if (items.length === 0) return;
            const active = items.indexOf(document.activeElement);
            this.focusOption(active === -1 ? items.length - 1 : active - 1);
        },

        toggle() {
            this.open = !this.open;
            if (this.open) {
                this.$nextTick(() => this.focusSelectedOrFirst());
            }
        },

        close() {
            this.open = false;
        },

        openAndFocusFirst() {
            this.open = true;
            this.$nextTick(() => this.focusSelectedOrFirst());
        },

        openAndFocusLast() {
            this.open = true;
            this.$nextTick(() => this.focusLast());
        },

        focusSelectedOrFirst() {
            const items = this.getOptions();
            const selected = items.findIndex(
                (opt) => opt.getAttribute('aria-selected') === 'true',
            );
            this.focusOption(selected === -1 ? 0 : selected);
        },

        handleEscape() {
            if (this.open) {
                this.close();
                this.$refs.trigger.focus();
            }
        },

        pick(event) {
            const button = event.currentTarget;
            if (!button || button.getAttribute('aria-disabled') === 'true') return;
            this.applySelection(button, true);
            this.close();
            this.$refs.trigger.focus();
        },

        applySelection(button, fireChange) {
            const value = button.dataset.value ?? '';
            // Pull the label from the option's label span (not the full
            // text, which would include the indicator's aria-hidden text).
            const labelEl = button.querySelector('.mh-form-dropdown__option-label');
            const label = labelEl ? labelEl.textContent.trim() : button.textContent.trim();
            this.currentValue = value;
            this.currentLabel = label;
            // Update aria-selected on all options.
            this.getOptions().forEach((opt) => {
                opt.setAttribute(
                    'aria-selected',
                    opt === button ? 'true' : 'false',
                );
            });
            // Update the hidden input + dispatch native change so any
            // listeners (form validation, etc.) see the value change.
            if (this.$refs.hidden) {
                this.$refs.hidden.value = value;
                if (fireChange) {
                    this.$refs.hidden.dispatchEvent(
                        new Event('change', { bubbles: true }),
                    );
                }
            }
        },
    };
}
